import React, { useState } from "react";
import { StyleSheet, View, ScrollView, Text } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import { Course, CreateProjectSubmissionInput, Project, ProjectSubmission } from "../../../graphql/generated/graphql";
import { CustomHtmlContent } from "../CustomHTMLContent/CustomHTMLContent";

const ProjectProposalForm = ({
  courseInfo = null,
  projectFramework = null,
  onSubmit,
  loading = false,
  submissionData,
}: {
  courseInfo: Course;
  projectFramework: Project;
  onSubmit: (formData: CreateProjectSubmissionInput) => Promise<void>;
  loading: boolean;
  submissionData: ProjectSubmission;
}) => {
  const [formData, setFormData] = useState({
    projectId: projectFramework?.id || submissionData?.project?.id || "",
    proposal: submissionData?.proposal || "",
    approachMethodology: submissionData?.approachMethodology || "",
    scopeJustification: submissionData?.scopeJustification || "",
    expectedOutcomes: submissionData?.expectedOutcomes?.[0] || "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.proposal.trim()) {
      newErrors.proposal = "Your project proposal is required";
    }
    if (!formData.scopeJustification.trim()) {
      newErrors.scopeJustification = "Scope justification is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      await onSubmit({
        projectId: formData.projectId,
        proposal: formData.proposal,
        approachMethodology: formData.approachMethodology,
        scopeJustification: formData.scopeJustification,
        expectedOutcomes: [formData.expectedOutcomes],
      });
    } else {
      // Show a general error message to help users understand what's missing
      alert("Please fill out all required fields before submitting.");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  return (
    <View style={styles.container}>
      {/* Project Framework Section (Read-Only) */}
      {projectFramework && (
        <View style={styles.projectFramework}>
          <ScrollView style={styles.formContainer} showsVerticalScrollIndicator={true} nestedScrollEnabled={true}>
            <CustomText style={styles.sectionTitle} text="Project Framework" useTranslationText={false} />
            <View style={styles.frameworkContainer}>
              <View style={styles.frameworkItem}>
                <CustomText style={styles.frameworkLabel} text="Project:" useTranslationText={false} />
                <CustomText
                  style={styles.frameworkText}
                  text={projectFramework.name || "Untitled Project"}
                  useTranslationText={false}
                />
              </View>

              {projectFramework.description && (
                <View style={styles.frameworkItem}>
                  <CustomText style={styles.frameworkLabel} text="Description:" useTranslationText={false} />
                  <CustomHtmlContent htmlString={projectFramework.description} />
                </View>
              )}

              {projectFramework.scopeDocument && (
                <View style={styles.frameworkItem}>
                  <CustomText style={styles.frameworkLabel} text="Scope & Requirements:" useTranslationText={false} />
                  <CustomHtmlContent htmlString={projectFramework.scopeDocument} />
                </View>
              )}

              {projectFramework.deliverableTypes && projectFramework.deliverableTypes.length > 0 && (
                <View style={styles.frameworkItem}>
                  <CustomText style={styles.frameworkLabel} text="Deliverable Types:" useTranslationText={false} />
                  {projectFramework.deliverableTypes.map((deliverableType, index) => (
                    <span key={deliverableType}>
                      <Text style={styles.frameworkListItem}>{"• "}</Text>
                      <CustomText
                        key={index}
                        style={styles.frameworkListItem}
                        text={deliverableType}
                        useTranslationText={true}
                      />
                    </span>
                  ))}
                </View>
              )}

              {projectFramework.assessmentCriteria && projectFramework.assessmentCriteria.length > 0 && (
                <View style={styles.frameworkItem}>
                  <CustomText style={styles.frameworkLabel} text="Assessment Criteria:" useTranslationText={false} />
                  {projectFramework.assessmentCriteria.map((criteria, index) => (
                    <CustomText
                      key={index}
                      style={styles.frameworkListItem}
                      text={`• ${criteria}`}
                      useTranslationText={false}
                    />
                  ))}
                </View>
              )}

              {projectFramework.expectedOutcomes && projectFramework.expectedOutcomes.length > 0 && (
                <View style={styles.frameworkItem}>
                  <CustomText style={styles.frameworkLabel} text="Learning Objectives:" useTranslationText={false} />
                  {projectFramework.expectedOutcomes.map((outcome, index) => (
                    <CustomText
                      key={index}
                      style={styles.frameworkListItem}
                      text={`• ${outcome}`}
                      useTranslationText={false}
                    />
                  ))}
                </View>
              )}

              {projectFramework.dueDate && (
                <View style={styles.frameworkItem}>
                  <CustomText style={styles.frameworkLabel} text="Due Date:" useTranslationText={false} />
                  <CustomText
                    style={styles.frameworkText}
                    text={new Date(projectFramework.dueDate).toLocaleDateString()}
                    useTranslationText={false}
                  />
                </View>
              )}
            </View>
          </ScrollView>
        </View>
      )}

      {/* Student Proposal Section */}
      <View style={styles.proposalSection}>
        <ScrollView style={styles.formContainer} showsVerticalScrollIndicator={true} nestedScrollEnabled={true}>
          <CustomText style={styles.sectionTitle} text="Your Proposal" useTranslationText={false} />
          <CustomText
            style={styles.sectionSubtitle}
            text="Propose your plan in 3 parts: what you’ll deliver, the key steps, and why it meets the scope and rubric."
            useTranslationText={false}
          />

          <View style={styles.inputSpacing}>
            <CustomText style={styles.fieldLabel} text="Idea and Approach" useTranslationText={false} />
            <CustomTextInput
              label="Your Project Approach *"
              value={formData.proposal}
              onChangeText={(value) => handleInputChange("proposal", value)}
              placeholder="Describe your specific idea and approach to this project..."
              multiline
              numberOfLines={5}
              error={errors.proposal}
              maxLength={1500}
            />
          </View>

          <View style={styles.inputSpacing}>
            <CustomText style={styles.fieldLabel} text="Why this fits the scope" useTranslationText={false} />
            <CustomTextInput
              label="Scope Justification *"
              value={formData.scopeJustification}
              onChangeText={(value) => handleInputChange("scopeJustification", value)}
              placeholder="Explain why your approach fits within the project scope and constraints..."
              multiline
              numberOfLines={3}
              error={errors.scopeJustification}
              maxLength={800}
            />
          </View>

          <View style={styles.inputSpacing}>
            <CustomText style={styles.fieldLabel} text="What you hope to learn" useTranslationText={false} />
            <CustomTextInput
              label="Personal Learning Goals"
              value={formData.expectedOutcomes}
              onChangeText={(value) => handleInputChange("expectedOutcomes", value)}
              placeholder="What specific skills or knowledge do you hope to gain from this project?"
              multiline
              numberOfLines={3}
              maxLength={500}
            />
          </View>
          <View style={styles.footer}>
            <CustomButton
              text={loading ? "Submitting..." : "Submit for AI Review"}
              size="m"
              styleType="primary"
              onPress={handleSubmit}
              disabled={loading}
              loading={loading}
            />
          </View>
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: "calc(100vh - 54px)",
    minHeight: 0,
    display: "flex",
    flexDirection: "row",
    backgroundColor: "#ffffff",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    overflow: "hidden",
  },
  formContainer: {
    flex: 1,
    paddingHorizontal: 32,
    paddingVertical: 24,
  },
  projectFramework: {
    height: "100%",
    width: 360,
    borderRightWidth: 1,
    borderRightColor: "#EAECF0",
  },
  proposalSection: {
    height: "100%",
    flex: 1,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#101828",
    marginBottom: 8,
  },
  sectionSubtitle: {
    color: "#344054",
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 24,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 24,
  },
  frameworkContainer: {
    backgroundColor: "#F8FAFC",
    padding: 20,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  frameworkItem: {
    marginBottom: 16,
  },
  frameworkLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  frameworkText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#1E293B",
    lineHeight: 22,
  },
  frameworkListItem: {
    fontSize: 14,
    color: "#475569",
    lineHeight: 20,
    marginBottom: 4,
  },
  fieldLabel: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 12,
  },
  inputSpacing: {
    marginBottom: 20,
  },
});

export default ProjectProposalForm;
